import { Button } from "hec/components/cta";
import { getDetermineRouteTypeAndUrl } from "hec/hooks/useDetermineRouteTypeAndUrl";
import PropTypes from "prop-types";

import type { RegisteredComponent } from "@builder.io/sdk-react";

import { type SiteId } from "~/hec/utils/site-id";
import { cn } from "~/lib/utils";
import { useRootLayoutData } from "~/root";

import { toCustomButtonStyle } from "./button.logic";
import {
  buttonFunctions,
  coreButtonInputs,
  coreButtonPropTypes,
  hasCustomUrl,
  withCustomUrlInputs,
} from "./cta.helper";
import type { BuilderButtonProps } from "./types";
import useFetchPageDataForCta from "./useFetchPageDataForCta";

const BuilderButton = ({
  disabled,
  variant,
  buttonColor,
  buttonHoverColor,
  openInNewTab,
  children,
  clickFunction = buttonFunctions.linkToPage,
  scrollTargetId,
  attributes,
  isInternalLink,
  isRevShare,
  pageReference,
  queryParams,
  externalLinkReference,
  customUrl,
  customUrlIsSfra,
  dataTestId,
}: BuilderButtonProps) => {
  const {
    className: classNameAttribute,
    style: styleAttribute,
    ...restAttributes
  } = attributes || {};

  // A non-empty customUrl overrides the page reference — skip the reference
  // fetch entirely (hook still runs unconditionally per the hooks rule). Same
  // pattern as `linkWrapper.tsx`.
  const skipReferences = hasCustomUrl(customUrl);
  const { pageRef, externalLinkRef, isResolved } = useFetchPageDataForCta({
    isInternalLink,
    pageReference: skipReferences ? undefined : pageReference,
    externalLinkReference: skipReferences ? undefined : externalLinkReference,
  });

  const { site } = useRootLayoutData();
  const { routeType, url } = getDetermineRouteTypeAndUrl({
    clickFunction,
    isInternalLink,
    // This component has no Suspense boundary, so before COM-198 an unresolved
    // reference arrived here as a PENDING PROMISE and every field read off it
    // came back undefined — a url-less CTA (the SFCC-11355 failure class). The
    // Builder page loader now inlines CTA refs server-side, so in practice these
    // are resolved objects. The guard keeps the old url-less output for the
    // residual streaming case instead of handing a promise to a function typed
    // for plain data.
    pageReference: isResolved ? pageRef : undefined,
    externalLinkReference: isResolved ? externalLinkRef : undefined,
    customUrl,
    customUrlIsSfra,
    siteId: site.id as SiteId,
  });

  // Map Builder variants to LinkRender variants
  // Builder exposes "primary", "secondary" and "accent"; LinkRender supports
  // those plus the link-style variants ("bold", "normal", etc.).
  const mappedVariant = variant as
    | "primary"
    | "secondary"
    | "accent"
    | "custom"
    | "bold"
    | "normal"
    | "breadcrumbLink"
    | "navLink"
    | undefined;

  return (
    <Button
      ctaType="button"
      disabled={disabled}
      variant={mappedVariant}
      openInNewTab={openInNewTab}
      clickFunction={clickFunction}
      scrollTargetId={scrollTargetId}
      // COM-884: 28px left/right by default (the shared CTA styles say 18px).
      // Last in the merge, so it wins over that; the Style tab still applies.
      className={cn("px-[28px]", classNameAttribute)}
      style={toCustomButtonStyle(
        variant,
        buttonColor,
        buttonHoverColor,
        styleAttribute,
      )}
      isRevShare={isRevShare}
      routeType={routeType ?? undefined}
      url={url ?? undefined}
      queryParams={queryParams}
      dataTestId={dataTestId}
      {...restAttributes}
      data-testid={dataTestId || "button-Button"}
    >
      {children}
    </Button>
  );
};

BuilderButton.propTypes = {
  className: PropTypes.string,
  variant: PropTypes.string,
  disabled: PropTypes.bool,
  ...coreButtonPropTypes,
};

export const ButtonComponent = BuilderButton;

export const ButtonDefinition = {
  component: BuilderButton,
  name: "Core:Button",
  override: true,
  noWrap: true,
  canHaveChildren: true,
  defaultChildren: [
    {
      "@type": "@builder.io/sdk:Element",
      component: {
        name: "Text",
        options: { text: "Click me" },
      },
    },
  ],
  childRequirements: {
    message: "You can only put Text, Heading and Icons into Button component",
    query: {
      "component.name": { $in: ["Text", "IconPicker", "Heading"] },
    },
  },
  inputs: [
    {
      name: "variant",
      type: "string",
      enum: ["primary", "secondary", "accent", "custom"],
      defaultValue: "primary",
    },
    // COM-884: shown for "custom" only; blank (or translucent) falls back to
    // the site's primary colors.
    {
      name: "buttonColor",
      friendlyName: "Button color",
      type: "color",
      showIf: "options.get('variant') === 'custom'",
    },
    {
      name: "buttonHoverColor",
      friendlyName: "Hover color",
      type: "color",
      showIf: "options.get('variant') === 'custom'",
    },
    {
      name: "disabled",
      type: "boolean",
      defaultValue: false,
    },
    // Custom URL + "Is SFRA" spliced in after "isInternalLink" (COM-214).
    ...withCustomUrlInputs(coreButtonInputs),
  ],
} satisfies RegisteredComponent;
